"use client";

import { CheckIcon } from "@heroicons/react/20/solid";
import { FlagIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib";
import { PulseRing } from "../pulse-ring";

export type CirclesProgressStepStatus =
	| "complete"
	| "current"
	| "open"
	| "focus"
	| "upcoming";

export interface CirclesProgressNavStep {
	id: string;
	name: string;
	status: CirclesProgressStepStatus;
	/** Default inner glyph is a dot; use `flag` for scenario-wide milestones. */
	stepGlyph?: "dot" | "flag";
}

export interface CirclesProgressNavProps {
	steps: readonly CirclesProgressNavStep[];
	ariaLabel: string;
	className?: string;
	/** Wrap each step control (e.g. tooltip, analytics). Default: identity. */
	decorateStep?: (args: {
		step: CirclesProgressNavStep;
		stepIndex: number;
		children: ReactNode;
	}) => ReactNode;
}

export function CirclesProgressNav({
	steps,
	ariaLabel,
	className,
	decorateStep = ({ children }) => children,
}: CirclesProgressNavProps) {
	return (
		<nav aria-label={ariaLabel} className={cn("w-full", className)}>
			<ol className="flex w-full items-center">
				{steps.map((step, stepIdx) => {
					const useFlag = step.stepGlyph === "flag";
					const connectorToNext =
						stepIdx !== steps.length - 1 ? (
							<div
								aria-hidden
								className={cn(
									"pointer-events-none absolute top-1/2 left-1/2 z-0 h-0.5 w-full -translate-y-1/2",
									step.status === "complete"
										? "bg-violet-600 dark:bg-violet-500"
										: "bg-gray-200 dark:bg-white/15",
								)}
							/>
						) : null;

					const control =
						step.status === "complete" ? (
							<button
								type="button"
								className="relative z-[5] flex size-8 items-center justify-center rounded-full bg-violet-600 hover:bg-violet-900 dark:bg-violet-500 dark:hover:bg-violet-400"
							>
								<CheckIcon
									aria-hidden
									className="size-5 text-white"
								/>
								<span className="sr-only">{step.name}</span>
							</button>
						) : step.status === "current" ||
							step.status === "open" ||
							step.status === "focus" ? (
							<div className="relative z-[5]">
								{step.status === "focus" ? (
									<PulseRing
										className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
										sizePx={44}
										durationS={2.8}
										borderColor="rgba(139, 92, 246, 0.6)"
									/>
								) : null}
								<button
									type="button"
									aria-current={
										step.status === "focus" ||
										step.status === "current"
											? "step"
											: undefined
									}
									className="relative flex size-8 items-center justify-center rounded-full border-2 border-violet-600 bg-white dark:border-violet-500 dark:bg-gray-900"
								>
									{useFlag ? (
										<FlagIcon
											aria-hidden
											className="size-3.5 shrink-0 text-violet-600 dark:text-violet-500"
											strokeWidth={2.25}
										/>
									) : (
										<span
											aria-hidden
											className="size-2.5 rounded-full bg-violet-600 dark:bg-violet-500"
										/>
									)}
									<span className="sr-only">{step.name}</span>
								</button>
							</div>
						) : (
							<button
								type="button"
								className="group relative z-[5] flex size-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white hover:border-gray-400 dark:border-white/15 dark:bg-gray-900 dark:hover:border-white/25"
							>
								{useFlag ? (
									<FlagIcon
										aria-hidden
										className="size-3.5 shrink-0 text-gray-400 group-hover:text-gray-500 dark:text-white/25 dark:group-hover:text-white/35"
										strokeWidth={2}
									/>
								) : (
									<span
										aria-hidden
										className="size-2.5 rounded-full bg-transparent group-hover:bg-gray-300 dark:group-hover:bg-white/15"
									/>
								)}
								<span className="sr-only">{step.name}</span>
							</button>
						);

					return (
						<li
							key={step.id}
							className={cn(
								"relative flex min-w-0 flex-1 items-center justify-center overflow-visible",
							)}
						>
							{connectorToNext}
							{decorateStep({
								step,
								stepIndex: stepIdx,
								children: control,
							})}
						</li>
					);
				})}
			</ol>
		</nav>
	);
}
